import { useCallback } from 'react';

import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type { UploaderMethodProps } from 'src/app/shared/components/imageUpload';

import { createEndorsement } from '../../api/createEndorsement';
import { getErrorType, getPhotoUrl } from '../helpers';
import type { EndorsementFormData } from '../types';

import type { EndorsementError } from './useEndorsementForm';

type SubmitParams = {
	petitionId: string;
	formData: EndorsementFormData;
	utilityContext: UtilityContext;
	track: (event: string, data?: Record<string, unknown>) => Promise<boolean>;
	onSuccess: () => void;
	onError: (error: EndorsementError) => void;
	setIsSubmitting: (value: boolean) => void;
	uploader: UploaderMethodProps;
	image: string | undefined;
	trackSubmitClick?: boolean;
};

function buildEndorsementInput(petitionId: string, formData: EndorsementFormData, photoUrl?: string) {
	return {
		petitionId,
		name: formData.name,
		title: formData.title || undefined,
		url: formData.url || undefined,
		content: formData.statement || undefined,
		...(photoUrl && { photo: { url: photoUrl } }),
	};
}

function handleSubmitError(
	track: SubmitParams['track'],
	onError: SubmitParams['onError'],
	msg: string,
	type: EndorsementError['type'],
) {
	void track('starter_dashboard_endorsement_create_error', { error: msg });
	onError({ message: msg, type });
}

// eslint-disable-next-line max-lines-per-function
export function useEndorsementSubmit(params: SubmitParams) {
	const {
		petitionId,
		formData,
		utilityContext,
		track,
		onSuccess,
		onError,
		setIsSubmitting,
		trackSubmitClick = false,
		uploader,
		image,
	} = params;

	const submit = useCallback(async () => {
		setIsSubmitting(true);
		if (trackSubmitClick) {
			void track('starter_dashboard_endorsement_submit_click');
		}
		try {
			const uploadResult = await getPhotoUrl(image, uploader);
			if (uploadResult && !uploadResult.success) {
				handleSubmitError(track, onError, uploadResult.error, 'SERVER_ERROR');
				return;
			}
			const input = buildEndorsementInput(petitionId, formData, uploadResult?.url);
			const response = await createEndorsement(input, utilityContext);
			if (response.success) {
				void track('starter_dashboard_endorsement_create_success');
				onSuccess();
			} else {
				const errorMsg = response.errorMessage || 'Failed to create endorsement';
				handleSubmitError(track, onError, errorMsg, getErrorType(errorMsg));
			}
		} catch (e) {
			handleSubmitError(track, onError, e instanceof Error ? e.message : 'Unexpected error', 'NETWORK_ERROR');
		} finally {
			setIsSubmitting(false);
		}
	}, [
		petitionId,
		formData,
		utilityContext,
		track,
		onSuccess,
		onError,
		setIsSubmitting,
		trackSubmitClick,
		uploader,
		image,
	]);

	return { submit };
}
